import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  CATALOG_ITEM_TYPES,
  CatalogConflictError,
  CatalogValidationError,
  MAX_AMOUNT,
  useSaveCatalogItem,
  type CatalogCategory,
  type CatalogItem,
  type CatalogItemType,
} from '@/routes/pricebook/usePricebook';

type Field = 'name' | 'defaultPrice' | 'cost' | 'categoryId' | 'isStandardLabor';

/**
 * Add a price-book item, or edit one (M2-02).
 *
 * Prices are held as the STRINGS the person typed and converted only on save:
 * `Number('')` is 0 (CC-21's note), so a cleared price box would otherwise
 * save as a free service rather than being refused.
 *
 * The category picker offers active categories only — plus, when editing, the
 * retired one the item is already filed under, labelled as retired, so opening
 * an old item to fix its price does not silently re-file it. The API accepts
 * exactly that and refuses moving an item INTO a retired category.
 */
export function CatalogItemFormDialog({
  item,
  categories,
  onClose,
  onAnnounce,
}: {
  /** Undefined when adding. */
  item?: CatalogItem;
  /** Active categories, in display order. */
  categories: CatalogCategory[];
  onClose: () => void;
  /** Told what happened, already translated, for the screen's live region (M1-27's rule). */
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const fieldId = useId();
  const save = useSaveCatalogItem(item?.id);

  const [type, setType] = useState<CatalogItemType>((item?.type as CatalogItemType) ?? 'Service');
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [price, setPrice] = useState(item ? String(Number(item.defaultPrice)) : '');
  const [cost, setCost] = useState(item ? String(Number(item.cost)) : '');
  const [taxable, setTaxable] = useState(item?.taxable ?? true);
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? '');
  // M2-33 (Kyle, Q8 = B): the one labor item a technician's override replaces.
  const [standardLabor, setStandardLabor] = useState(item?.isStandardLabor ?? false);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState('');

  const retiredCurrent =
    item?.categoryId && item.categoryIsRetired && !categories.some((c) => c.id === item.categoryId)
      ? { id: item.categoryId, label: item.categoryLabel ?? '' }
      : null;

  /** A money box's value, or the sentence saying why it is not one. */
  function money(value: string, label: string): number | string {
    const trimmed = value.trim();
    if (trimmed === '') {
      return t('pricebook.form.amountRequired', { field: label });
    }
    const amount = Number(trimmed);
    if (!Number.isFinite(amount) || amount < 0) {
      return t('pricebook.form.amountInvalid', { field: label });
    }
    if (amount > MAX_AMOUNT) {
      return t('pricebook.form.amountTooHigh', { field: label });
    }
    if (Math.round(amount * 100) / 100 !== amount) {
      return t('pricebook.form.amountCents', { field: label });
    }
    return amount;
  }

  function submit() {
    const next: Partial<Record<Field, string>> = {};
    if (!name.trim()) {
      next.name = t('pricebook.form.nameRequired');
    }
    const parsedPrice = money(price, t('pricebook.form.price'));
    const parsedCost = money(cost, t('pricebook.form.cost'));
    if (typeof parsedPrice === 'string') {
      next.defaultPrice = parsedPrice;
    }
    if (typeof parsedCost === 'string') {
      next.cost = parsedCost;
    }
    setErrors(next);
    setFormError('');
    if (
      Object.keys(next).length > 0 ||
      typeof parsedPrice === 'string' ||
      typeof parsedCost === 'string'
    ) {
      return;
    }

    save.mutate(
      {
        type,
        name: name.trim(),
        description: description.trim() || null,
        defaultPrice: parsedPrice,
        cost: parsedCost,
        taxable,
        categoryId: categoryId || null,
        // M2-33: only a labor item can carry it (the API refuses otherwise),
        // so changing the type away from Labor clears it.
        isStandardLabor: type === 'Labor' && standardLabor,
      },
      {
        onSuccess: (saved) => {
          onAnnounce(
            item
              ? t('pricebook.form.saved', { name: saved.name })
              : t('pricebook.form.created', { name: saved.name }),
          );
          onClose();
        },
        onError: (err) => {
          if (err instanceof CatalogValidationError) {
            // The server's own sentence, on the field it names — never a
            // paraphrase that can drift from the rule it applied.
            const mapped = {
              name: err.forField('Name'),
              defaultPrice: err.forField('DefaultPrice'),
              isStandardLabor: err.forField('IsStandardLabor'),
              cost: err.forField('Cost'),
              categoryId: err.forField('CategoryId'),
            };
            setErrors(mapped);
            // ⚠️ A refusal for a field this form does not map (Description,
            // Type, a rule added later) would otherwise clear every error and
            // show nothing — Save would look like it did nothing at all.
            if (!Object.values(mapped).some(Boolean)) {
              setFormError(t('pricebook.form.saveFailedWithReason', { reason: err.message }));
            }
            return;
          }
          // M2-33 review: a 409 is the server saying why, in a sentence (a labor
          // item with unbilled time keeps its kind) — shown, not "try again".
          if (err instanceof CatalogConflictError) {
            setFormError(t('pricebook.form.saveFailedWithReason', { reason: err.message }));
            return;
          }
          setFormError(t('pricebook.form.saveFailed'));
        },
      },
    );
  }

  function errorFor(field: Field) {
    const message = errors[field];
    return message ? (
      <p
        id={`${fieldId}-${field}-error`}
        role="alert"
        className="mt-1 text-sm text-[var(--color-destructive)]"
      >
        {message}
      </p>
    ) : null;
  }

  function describedBy(field: Field) {
    return errors[field] ? `${fieldId}-${field}-error` : undefined;
  }

  return (
    <Dialog
      titleId={titleId}
      title={item ? t('pricebook.form.editTitle') : t('pricebook.form.addTitle')}
      onClose={onClose}
      testId="catalog-item-form-dialog"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label htmlFor={`${fieldId}-name`} className="text-sm font-medium">
            {t('pricebook.form.name')}
          </label>
          <input
            id={`${fieldId}-name`}
            className={FIELD_CLASS}
            value={name}
            maxLength={200}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={describedBy('name')}
            onChange={(event) => setName(event.target.value)}
          />
          {errorFor('name')}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor={`${fieldId}-type`} className="text-sm font-medium">
              {t('pricebook.form.type')}
            </label>
            <select
              id={`${fieldId}-type`}
              className={FIELD_CLASS}
              value={type}
              onChange={(event) => setType(event.target.value as CatalogItemType)}
            >
              {CATALOG_ITEM_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`pricebook.type.${value}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor={`${fieldId}-category`} className="text-sm font-medium">
              {t('pricebook.form.category')}
            </label>
            <select
              id={`${fieldId}-category`}
              className={FIELD_CLASS}
              value={categoryId}
              aria-invalid={errors.categoryId ? true : undefined}
              aria-describedby={describedBy('categoryId')}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">{t('pricebook.uncategorized')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
              {retiredCurrent ? (
                <option value={retiredCurrent.id}>
                  {t('pricebook.retiredCategoryOption', { label: retiredCurrent.label })}
                </option>
              ) : null}
            </select>
            {errorFor('categoryId')}
          </div>

          <div>
            <label htmlFor={`${fieldId}-price`} className="text-sm font-medium">
              {type === 'Labor' ? t('pricebook.form.pricePerHour') : t('pricebook.form.price')}
            </label>
            <input
              id={`${fieldId}-price`}
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_AMOUNT}
              step="0.01"
              className={FIELD_CLASS}
              value={price}
              aria-invalid={errors.defaultPrice ? true : undefined}
              aria-describedby={describedBy('defaultPrice')}
              onChange={(event) => setPrice(event.target.value)}
            />
            {errorFor('defaultPrice')}
          </div>

          <div>
            <label htmlFor={`${fieldId}-cost`} className="text-sm font-medium">
              {t('pricebook.form.cost')}
            </label>
            <input
              id={`${fieldId}-cost`}
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_AMOUNT}
              step="0.01"
              className={FIELD_CLASS}
              value={cost}
              aria-invalid={errors.cost ? true : undefined}
              aria-describedby={[describedBy('cost'), `${fieldId}-cost-hint`]
                .filter(Boolean)
                .join(' ')}
              onChange={(event) => setCost(event.target.value)}
            />
            {errorFor('cost')}
            {/* Said because the office will wonder who sees it: cost drives
                margin reporting and never reaches a customer (M2-07). */}
            <p
              id={`${fieldId}-cost-hint`}
              className="mt-1 text-xs text-[var(--color-muted-foreground)]"
            >
              {t('pricebook.form.costHint')}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <input
            id={`${fieldId}-taxable`}
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={taxable}
            onChange={(event) => setTaxable(event.target.checked)}
          />
          <div>
            <label htmlFor={`${fieldId}-taxable`} className="text-sm font-medium">
              {t('pricebook.form.taxable')}
            </label>
            {/* The OD-19 distinction, in the office's words: unticking says
                "never taxed anywhere", not "not taxed here" — the property's
                jurisdiction decides the rest. */}
            <p className="text-xs text-[var(--color-muted-foreground)]">
              {t('pricebook.form.taxableHint')}
            </p>
          </div>
        </div>

        {type === 'Labor' ? (
          <div className="flex items-start gap-2">
            <input
              id={`${fieldId}-standard`}
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0"
              checked={standardLabor}
              aria-describedby={[describedBy('isStandardLabor'), `${fieldId}-standard-hint`]
                .filter(Boolean)
                .join(' ')}
              onChange={(event) => setStandardLabor(event.target.checked)}
            />
            <div>
              <label htmlFor={`${fieldId}-standard`} className="text-sm font-medium">
                {t('pricebook.form.standardLabor')}
              </label>
              <p
                id={`${fieldId}-standard-hint`}
                className="text-xs text-[var(--color-muted-foreground)]"
              >
                {t('pricebook.form.standardLaborHint')}
              </p>
              {errorFor('isStandardLabor')}
            </div>
          </div>
        ) : null}

        <div>
          <label htmlFor={`${fieldId}-description`} className="text-sm font-medium">
            {t('pricebook.form.description')}
          </label>
          <textarea
            id={`${fieldId}-description`}
            className={FIELD_CLASS}
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        {formError ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {formError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
